import { doesNotReject } from 'assert';

import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  FlightItineraryWithExtensions,
  Traveller,
  ServiceProvider,
  clone
} from '@sabstravtech/obtservices/base';
import {
  EnterpriseSearchService,
  UserService,
  StorageService,
  FlightSeatMapSeats,
  InputFlightJourney
} from '@sabstravtech/obtservices/angular';

import { LightningModalTypes } from '../../../../core/classes/modal-types.enum';
import { BaseModalComponent } from '../../../../modal/base-modal.component';
import { LightningUserFavorurite } from '../../../../core/classes/user-favourite.enum';

@Component({
  selector: 'app-flight-extras-dialog',
  templateUrl: './flight-extras-dialog.component.html',
  styleUrls: ['./flight-extras-dialog.component.scss'],
  standalone: false
})
export class FlightExtrasDialogComponent extends BaseModalComponent implements OnInit {
  // Inputs
  public flight: FlightItineraryWithExtensions = null;
  public users: Traveller[] = [];
  public travellers: Traveller[] = [];
  activeFlightID: { [key: string]: string } = {};
  selectedSeat: FlightSeatMapSeats[] = [];
  disableButton = true;
  selectedSeatWithId: { [x: string]: FlightSeatMapSeats[] } = {};
  loyaltyWithId: {
    [x: string]: { loyalty: string; index: number; userId: number; loyaltyProgram: string }[];
  } = {};
  ancillaryOptions: InputFlightJourney[];
  additionalDetails: {
    [key: string]: any;
  };
  notValid = false;
  processedTravellers: string[] = [];
  notProcessedTravellers: Traveller[] = [];
  changesMade: string[] = [];
  travellerSeat: any = [];
  displayItinerary = false;
  updatingSeats = false;
  constructor(
    public activeModal: NgbActiveModal,
    public searchService: EnterpriseSearchService,
    public userService: UserService,
    public storageService: StorageService
  ) {
    super(activeModal);
  }

  ngOnInit() {
    if (!this.users?.length) {
      this.getTravellers();
    } else {
      this.travellers = this.users;
      this.updatingSeats = true;
    }
    const newExtras = {
      advancedAncillaryOptions: [],
      baggageAllowance: [],
      reducedMobillityAssistance: [],
      flightExtras: [],
      luggagePerBooking: true,
      luggagePerPax: true,
      ancillaryPerBooking: true
    };
    // Initialize additional details object
    this.additionalDetails = this.flight.outboundFlights.reduce(
      (obj, item) => Object.assign(obj, { [item.id]: { ...newExtras } }),
      {}
    );
    this.travellers.forEach((traveller) => {
      this.activeFlightID[traveller.id] = this.flight.outboundFlights[0].id;
    });
    if (this.flight.inboundFlights?.length) {
      Object.assign(
        this.additionalDetails,
        this.flight.inboundFlights.reduce(
          (obj, item) => Object.assign(obj, { [item.id]: { ...newExtras } }),
          {}
        )
      );
    }

    // Push the 1st passenger to the list
    this.notProcessedTravellers.push(this.travellers[0]);
    this.displayItinerary = this.userService.isBasketReplacementUserFavouriteSet();
    // basically add the seat stuff here
    if (this.updatingSeats) {
      this.flight.outboundFlights.forEach((flight) => {
        if (flight.additional.seat?.length) {
          flight.additional.seat.forEach((s, index) => {
            this.getSeat({ selectedSeat: s, id: flight.id, travellerId: s.userId }, index);
          });
        }
      });
      this.flight.inboundFlights.forEach((flight) => {
        if (flight.additional.seat?.length) {
          flight.additional.seat.forEach((s, index) => {
            this.getSeat({ selectedSeat: s, id: flight.id, travellerId: s.userId }, index);
          });
        }
      });
    }
  }

  getTravellers() {
    this.subscribe(this.searchService.travellerInformation, (travellers: Traveller[]) => {
      this.travellers = travellers;
    });
    console.log(this.travellers);
  }

  addToBasket() {
    console.log(this.loyaltyWithId);
    // ENT-11650 don't replace baggage if it wasn't an option in the modal
    const seatMapConfigs = this.userService.getUserFavoriteObject<{
      GDS: { showSeatMaps: boolean };
      SabreLowCost: { showSeatMaps: boolean; showExtras: boolean };
      LCC: { showSeatMaps: boolean; showExtras: boolean };
      NDC: { showSeatMaps: boolean };
    }>(LightningUserFavorurite.FlightResultsConfiguration);
    const isSabreRyanair =
      this.flight.source === ServiceProvider.SabreFlight &&
      [...this.flight.outboundFlights, ...this.flight.inboundFlights].some(
        (flight) =>
          flight.marketingCarrierName === 'Ryanair' || flight.operatingCarrierName === 'Ryanair'
      );

    const showExtras = isSabreRyanair
      ? seatMapConfigs?.SabreLowCost?.showExtras
      : this.flight.source === (ServiceProvider.Travelfusion as string)
        ? seatMapConfigs?.LCC?.showExtras
        : true;
    const details = Object.keys(this.additionalDetails).reduce((acc, detail) => {
      const { ancillaryPerBooking, luggagePerBooking, luggagePerPax, reducedMobillityAssistance } =
        this.additionalDetails[detail];
      acc[detail] = {
        ancillaryPerBooking,
        luggagePerBooking,
        luggagePerPax,
        reducedMobillityAssistance
      };
      return acc;
    }, {});
    this.activeModal.close({
      seats: this.selectedSeatWithId,
      valid: true,
      additionalDetails: showExtras ? this.additionalDetails : details,
      loyalty: this.loyaltyWithId
    });
  }

  updateSeatMap() {
    this.activeModal.close({
      seats: this.selectedSeatWithId,
      valid: true,
      additionalDetails: this.additionalDetails,
      loyalty: this.loyaltyWithId
    });
  }

  getSeat(seat: { selectedSeat: FlightSeatMapSeats; id: string; travellerId: string }, index = 0) {
    const selectedSeat = seat.selectedSeat;
    this.selectedSeat[index] = selectedSeat;
    // obviusly this is just the most recently selected seat per pax
    // I need to have the pax/flight, replace traveller id with something to represent flight
    if (this.travellerSeat[index]) {
      this.travellerSeat[index][seat.id] = selectedSeat?.row
        ? selectedSeat?.row + selectedSeat?.column
        : null;
    } else {
      this.travellerSeat[index] = {
        [seat.id]: selectedSeat.row + selectedSeat.column
      };
    }
    if (!this.selectedSeatWithId[seat.id]) {
      this.selectedSeatWithId[seat.id] = [];
    }
    if (selectedSeat) {
      if (!this.changesMade.includes('seat')) {
        this.changesMade.push('seat');
      }
    } else {
      this.changesMade.splice(this.changesMade.indexOf('seat'), 1);
    }
    this.selectedSeatWithId[seat.id][index] = selectedSeat;
    if (!selectedSeat) {
      this.selectedSeatWithId[seat.id] = this.selectedSeatWithId[seat.id].filter((x) => x);
    }
    // doing this otherwise change is not detected
    this.selectedSeatWithId = { ...this.selectedSeatWithId };

    // this.isValid();
  }

  getLoyalty(loyalty, index = 0, travellerId = 0) {
    if (!this.loyaltyWithId[loyalty.id]) {
      this.loyaltyWithId[loyalty.id] = [];
    }

    if (this.loyaltyWithId[loyalty.id].filter((l) => l.index === index).length) {
      this.loyaltyWithId[loyalty.id][
        this.loyaltyWithId[loyalty.id].findIndex((l) => l.index === index)
      ].loyalty = loyalty.loyalty;

      this.loyaltyWithId[loyalty.id][
        this.loyaltyWithId[loyalty.id].findIndex((l) => l.index === index)
      ].loyaltyProgram = loyalty.loyaltyProgram;
    } else {
      this.loyaltyWithId[loyalty.id].push({
        loyalty: loyalty.loyalty,
        index: index,
        userId: travellerId,
        loyaltyProgram: loyalty.loyaltyProgram
      });
    }
  }

  flightExtras(
    extras: {
      data: any;
      flightIds: [string];
      updatingFlightId: string;
      travellerId: string;
      key: string;
      luggagePerBooking: boolean;
      luggagePerPax: boolean;
      ancillaryPerBooking: boolean;
    },
    travellerId
  ) {
    const additionalDetails = clone(extras.data);

    if (additionalDetails.flightExtras) {
      additionalDetails.flightExtras.userId = travellerId;
    }
    if (additionalDetails.advancedAncillaryOptions) {
      additionalDetails.advancedAncillaryOptions.userId = travellerId;
    }
    if (additionalDetails.baggageAllowance) {
      additionalDetails.baggageAllowance.userId = travellerId;
    }
    if (additionalDetails.reducedMobillityAssistance) {
      additionalDetails.reducedMobillityAssistance.userId = travellerId;
    }

    const { key } = extras;

    extras.flightIds.forEach((flightId) => {
      if (additionalDetails[key] && this.additionalDetails[flightId][key]) {
        // Add the same extra to the other legs on the same journey
        if (additionalDetails[key].id) {
          if (!this.changesMade.includes(key)) {
            this.changesMade.push(key);
          }
          const userId = additionalDetails[key].userId;
          const existingExtras = this.additionalDetails[flightId][key];

          // Check if an item with the same userId exists
          const newExtras = existingExtras.some((extra) => extra.userId === userId)
            ? existingExtras.map((extra) =>
                // Replace if userId matches
                extra.userId === userId ? { ...additionalDetails[key] } : extra
              )
            : [...existingExtras, { ...additionalDetails[key] }];

          Object.assign(this.additionalDetails[flightId], {
            [key]: newExtras
          });

          Object.assign(this.additionalDetails[flightId], {
            ['luggagePerBooking']: extras.luggagePerBooking
          });

          Object.assign(this.additionalDetails[flightId], {
            ['luggagePerPax']: extras.luggagePerPax
          });

          Object.assign(this.additionalDetails[flightId], {
            ['ancillaryPerBooking']: extras.ancillaryPerBooking
          });
        } else {
          this.changesMade.splice(this.changesMade.indexOf(key), 1);
          // Remove extras when choosing None
          const oldExtras = this.additionalDetails[flightId][key].findIndex(
            (extr: any) => extr.userId === extras.travellerId
          );
          this.additionalDetails[flightId][key].splice(oldExtras, 1);
        }
      }
    });
    console.log(this.additionalDetails);
  }

  setFlightNotValid(notValid: boolean) {
    this.notValid = notValid;
  }

  processedTab(travellerId: string) {
    // Process one by one until processedTravellers is full of travellers.
    this.processedTravellers.push(travellerId);

    const firstOfNotProcessedPax = this.travellers.filter(
      (x) => !this.processedTravellers.includes(x.id)
    )[0];

    if (firstOfNotProcessedPax) {
      this.notProcessedTravellers.push(firstOfNotProcessedPax);
    }
  }

  loyaltyChange(isChange: boolean) {
    if (isChange && !this.changesMade.includes('loyalty')) {
      this.changesMade.push('loyalty');
    } else if (!isChange) {
      this.changesMade.splice(this.changesMade.indexOf('loyalty'), 1);
    }
  }

  setActiveFlightID(activeFlightInfo: { flightId: string; travellerId: string }): void {
    const { flightId, travellerId } = activeFlightInfo;
    this.activeFlightID[travellerId] = flightId;
  }
}

LightningModalTypes.FlightExtrasDialogComponent.component = FlightExtrasDialogComponent;
